import React, { useState, useCallback, useContext, useMemo } from 'react';
import {
  Box, Typography, Stack, Chip, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Drawer, Divider, Skeleton, InputAdornment,
  Avatar, TablePagination, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Close, Receipt, Print,
  AccessTime, Person, ShoppingCart, CurrencyRupee,
  CalendarMonth, ExpandMore, Tune, Menu as MenuIcon, NoteAlt,
  AssignmentReturn, Search, SwapHoriz,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useListOrdersQuery, useGetOrderQuery } from '../services/orderApi';
import { useListStoreReturnsQuery, useListOrderReturnsQuery } from '../services/returnApi';
import { useGetOrderExchangesQuery } from '../services/exchangeApi';
import { useToast } from '../components/common/ToastProvider';
import { SidebarContext } from '../context/SidebarContext';
import ReturnItemsDrawer from '../components/pos/ReturnItemsDrawer';
import ReturnDetailDrawer from '../components/pos/ReturnDetailDrawer';
import ExchangeDetailDrawer from '../components/pos/ExchangeDetailDrawer';
import { usePrinterContext, browserPrintOrder } from '../context/PrinterContext';
import { mapOrderToReceipt } from '../components/pos/receipt/mapOrderToReceipt';
import { useGetReceiptTemplateQuery } from '../services/receiptTemplateApi';
import EinvoiceStatus from '../components/common/EinvoiceStatus';
import WhatsAppSection from '../components/pos/checkout/WhatsAppBillSection';
import { useGetCustomerQuery } from '../services/customerApi';

// Units sold on a bill (sum of quantities); '—' for an older API without it.
const fmtQty = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 3 }));

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  draft:     { label: 'Draft',     bgcolor: '#EFF6FF', color: '#2563EB', dot: '#3B82F6' },
  on_hold:   { label: 'On Hold',   bgcolor: '#FFFBEB', color: '#B45309', dot: '#F59E0B' },
  paid:      { label: 'Paid',      bgcolor: '#ECFDF5', color: '#059669', dot: '#10B981' },
  cancelled: { label: 'Cancelled', bgcolor: '#FEF2F2', color: '#DC2626', dot: '#EF4444' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, bgcolor: '#F1F5F9', color: '#64748B', dot: '#94A3B8' };
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1, py: 0.35, borderRadius: '6px', bgcolor: cfg.bgcolor,
    }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: cfg.dot, flexShrink: 0 }} />
      <Typography fontSize="0.7rem" fontWeight={700} color={cfg.color} lineHeight={1}>
        {cfg.label}
      </Typography>
    </Box>
  );
}

// ─── Amount display ───────────────────────────────────────────────────────────

function Amount({ value, size = '0.88rem', bold = 700, color = '#1E293B' }) {
  const num = parseFloat(value || 0);
  return (
    <Typography fontSize={size} fontWeight={bold} color={color} sx={{ fontVariantNumeric: 'tabular-nums' }}>
      ₹{num.toFixed(2)}
    </Typography>
  );
}

// ─── ReturnHistoryRow — shown inside OrderDetailDrawer's returns section ──────

function ReturnHistoryRow({ orderReturn, orderId, onOpenDetail }) {
  const methodColors = {
    cash: { bgcolor: '#EFF6FF', color: '#2563EB' },
    store_credit: { bgcolor: '#F5F3FF', color: '#7C3AED' },
    same_payment_mode: { bgcolor: '#EEF2FF', color: '#4F46E5' },
  };
  const methodLabels = { cash: 'Cash', store_credit: 'Store Credit', same_payment_mode: 'Same Payment Mode' };
  const methodCfg = methodColors[orderReturn.refund_method] || { bgcolor: '#F1F5F9', color: '#64748B' };
  const isCompleted = orderReturn.status === 'completed';

  return (
    <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', p: 1.75, bgcolor: '#FFF' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
        <Typography
          fontWeight={700} fontSize="0.82rem" color="#C2410C"
          onClick={onOpenDetail ? () => onOpenDetail(orderReturn) : undefined}
          sx={onOpenDetail ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : {}}
        >
          {orderReturn.return_number}
        </Typography>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          px: 1, py: 0.35, borderRadius: '6px',
          bgcolor: isCompleted ? '#ECFDF5' : '#FFFBEB',
        }}>
          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: isCompleted ? '#10B981' : '#F59E0B' }} />
          <Typography fontSize="0.7rem" fontWeight={700} color={isCompleted ? '#059669' : '#B45309'} lineHeight={1}>
            {isCompleted ? 'Completed ✓' : 'Draft ●'}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={methodLabels[orderReturn.refund_method] || orderReturn.refund_method}
            size="small"
            sx={{ fontWeight: 700, fontSize: '0.68rem', height: 20, ...methodCfg }}
          />
          {isCompleted && orderReturn.refund_amount && (
            <Typography fontWeight={700} fontSize="0.82rem" color="#DC2626">
              ₹{parseFloat(orderReturn.monetary_refund_amount ?? orderReturn.refund_amount).toFixed(2)}
            </Typography>
          )}
        </Box>
        <Typography fontSize="0.68rem" color="#94A3B8">
          {new Date(orderReturn.completed_at || orderReturn.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── ReturnCard — mobile card for Returns list ────────────────────────────────

function ReturnCard({ orderReturn, onClick }) {
  const methodColors = {
    cash: { bgcolor: '#EFF6FF', color: '#2563EB' },
    store_credit: { bgcolor: '#F5F3FF', color: '#7C3AED' },
    same_payment_mode: { bgcolor: '#EEF2FF', color: '#4F46E5' },
  };
  const methodLabels = { cash: 'Cash', store_credit: 'Store Credit', same_payment_mode: 'Same Payment Mode' };
  const methodCfg = methodColors[orderReturn.refund_method] || { bgcolor: '#F1F5F9', color: '#64748B' };
  const isCompleted = orderReturn.status === 'completed';

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 2, borderRadius: '12px', bgcolor: '#fff', border: '1px solid #E2E8F0',
        cursor: 'pointer',
        '&:active': { bgcolor: '#FFF7ED' },
        '&:hover': { borderColor: '#FDBA74', boxShadow: '0 2px 10px rgba(194,65,12,0.07)' },
        transition: 'all 0.15s',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography fontWeight={800} fontSize="0.9rem" color="#C2410C">
            {orderReturn.return_number}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            <AssignmentReturn sx={{ fontSize: 11, color: '#CBD5E1' }} />
            <Typography fontSize="0.68rem" color="#94A3B8">
              Order {orderReturn.order_number}
            </Typography>
          </Box>
        </Box>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          px: 1, py: 0.35, borderRadius: '6px',
          bgcolor: isCompleted ? '#ECFDF5' : '#FFFBEB',
        }}>
          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: isCompleted ? '#10B981' : '#F59E0B' }} />
          <Typography fontSize="0.7rem" fontWeight={700} color={isCompleted ? '#059669' : '#B45309'} lineHeight={1}>
            {isCompleted ? 'Completed ✓' : 'Draft ●'}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Chip
          label={methodLabels[orderReturn.refund_method] || orderReturn.refund_method}
          size="small"
          sx={{ fontWeight: 700, fontSize: '0.68rem', height: 20, ...methodCfg }}
        />
        <Box sx={{ textAlign: 'right' }}>
          {isCompleted && orderReturn.refund_amount ? (
            <Typography fontWeight={800} fontSize="0.95rem" color="#15803D">
              ₹{parseFloat(orderReturn.monetary_refund_amount ?? orderReturn.refund_amount).toFixed(2)}
            </Typography>
          ) : (
            <Typography fontSize="0.82rem" color="#94A3B8" fontStyle="italic">—</Typography>
          )}
          <Typography fontSize="0.65rem" color="#94A3B8">
            {new Date(orderReturn.completed_at || orderReturn.created_at).toLocaleDateString('en-IN', { dateStyle: 'short' })}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ─── ReturnsEmptyState ────────────────────────────────────────────────────────

function ReturnsEmptyState() {
  return (
    <Box sx={{ textAlign: 'center', py: 10 }}>
      <Box sx={{ width: 64, height: 64, borderRadius: '16px', bgcolor: '#FFF7ED', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
        <AssignmentReturn sx={{ fontSize: 28, color: '#FDBA74' }} />
      </Box>
      <Typography fontWeight={700} color="#64748B" fontSize="0.9rem" sx={{ mb: 0.5 }}>
        No returns found
      </Typography>
      <Typography fontSize="0.78rem" color="#94A3B8">
        Returns you process will appear here.
      </Typography>
    </Box>
  );
}

// ─── Exchange history row (inside OrderDetailDrawer) ─────────────────────────

const EXCHANGE_STATUS_CFG = {
  completed: { label: 'Completed', bgcolor: '#ECFDF5', color: '#059669', dot: '#10B981' },
  draft:     { label: 'Draft',     bgcolor: '#FFFBEB', color: '#B45309', dot: '#F59E0B' },
  cancelled: { label: 'Cancelled', bgcolor: '#FEF2F2', color: '#DC2626', dot: '#EF4444' },
};

function ExchangeHistoryRow({ exchange, onClick }) {
  const cfg = EXCHANGE_STATUS_CFG[exchange.status] || { label: exchange.status, bgcolor: '#F1F5F9', color: '#64748B', dot: '#94A3B8' };
  const dirLabels = { customer_pays: '↑ Pays', customer_refund: '↓ Refund', even: '= Even' };
  const dirColors = { customer_pays: '#2563EB', customer_refund: '#7C3AED', even: '#059669' };
  return (
    <Box
      onClick={onClick}
      sx={{
        border: '1px solid #E2E8F0', borderRadius: '10px', p: 1.75, bgcolor: '#FFF',
        cursor: 'pointer', '&:hover': { borderColor: '#A5B4FC', bgcolor: '#F5F3FF' }, transition: 'all 0.15s',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.35, borderRadius: '6px', bgcolor: cfg.bgcolor }}>
          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: cfg.dot, flexShrink: 0 }} />
          <Typography fontSize="0.7rem" fontWeight={700} color={cfg.color} lineHeight={1}>{cfg.label}</Typography>
        </Box>
        <Typography fontSize="0.68rem" color="#94A3B8">
          {new Date(exchange.completed_at || exchange.created_at).toLocaleDateString('en-IN', { dateStyle: 'short' })}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography fontSize="0.78rem" fontWeight={700} color={dirColors[exchange.settlement_direction] || '#64748B'}>
          {dirLabels[exchange.settlement_direction] || '—'}
        </Typography>
        {exchange.status !== 'cancelled' && exchange.net_settlement_amount != null ? (
          <Typography fontWeight={800} fontSize="0.85rem" color="#1E293B">
            ₹{parseFloat(exchange.net_settlement_amount).toFixed(2)}
          </Typography>
        ) : (
          <Typography fontSize="0.78rem" color="#94A3B8" fontStyle="italic">—</Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Order Detail Drawer ──────────────────────────────────────────────────────

function OrderDetailDrawer({ orderId, open, onClose, onOpenReturn, onOpenExchangeCreate }) {
  const showToast = useToast();
  const navigate = useNavigate();
  const { isConnected, silentPrintOrder } = usePrinterContext();
  const { data, isFetching } = useGetOrderQuery(orderId, { skip: !orderId || !open });
  const { data: receiptTemplate } = useGetReceiptTemplateQuery();
  const [returnDrawerOpen, setReturnDrawerOpen] = useState(false);
  const [detailTab, setDetailTab] = useState('returns'); // 'returns' | 'exchanges'
  const [selectedExchangeId, setSelectedExchangeId] = useState(null);
  const [exchangeDetailOpen, setExchangeDetailOpen] = useState(false);

  const { data: returnsData, isFetching: isReturnsLoading } = useListOrderReturnsQuery(
    { orderId },
    { skip: !open || !orderId }
  );
  const orderReturns = returnsData?.data?.order_returns || [];

  const { data: exchangesData, isFetching: isExchangesLoading } = useGetOrderExchangesQuery(
    { orderId },
    { skip: !open || !orderId }
  );
  const orderExchanges = exchangesData?.data?.exchanges || exchangesData?.exchanges || [];

  const order = data?.data?.order || data?.data;
  const receipt = useMemo(() => mapOrderToReceipt(data, receiptTemplate), [data, receiptTemplate]);
  const { data: customerRes } = useGetCustomerQuery(order?.customer_id, { skip: !open || !order?.customer_id });
  const customer = customerRes?.data?.customer || customerRes?.data;


  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: { xs: '100vw', sm: 420 }, maxWidth: '100vw', display: 'flex', flexDirection: 'column', bgcolor: '#F8FAFC' },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2.5, py: 2, bgcolor: '#fff', borderBottom: '1px solid #E2E8F0', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Receipt sx={{ color: '#4F46E5', fontSize: 18 }} />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                fontWeight={800}
                fontSize="0.92rem"
                color="#1E293B"
                sx={{ lineHeight: 1.25, wordBreak: 'break-word' }}
              >
                {isFetching ? 'Loading…' : order ? `Order ${order.order_number || order.reference_number}` : 'Order Detail'}
              </Typography>
              {order && !isFetching && (
                <Typography fontSize="0.68rem" color="#94A3B8" sx={{ mt: 0.25, lineHeight: 1.35 }}>
                  {new Date(order.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={onClose} sx={{ color: '#64748B', flexShrink: 0, mt: -0.25 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {order?.status === 'paid' && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AssignmentReturn sx={{ fontSize: 13 }} />}
                onClick={() => setReturnDrawerOpen(true)}
                sx={{
                  flex: 1,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  borderRadius: '8px',
                  color: '#C2410C',
                  borderColor: '#FED7AA',
                  bgcolor: '#FFF7ED',
                  '&:hover': { bgcolor: '#FFEDD5', borderColor: '#FDBA74' },
                }}
              >
                Return Items
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SwapHoriz sx={{ fontSize: 13 }} />}
                onClick={() => onOpenExchangeCreate && onOpenExchangeCreate(order)}
                sx={{
                  flex: 1,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  borderRadius: '8px',
                  color: '#7C3AED',
                  borderColor: '#DDD6FE',
                  bgcolor: '#F5F3FF',
                  '&:hover': { bgcolor: '#EDE9FE', borderColor: '#C4B5FD' },
                }}
              >
                Exchange
              </Button>
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<Print sx={{ fontSize: 14 }} />}
                onClick={async () => {
                  if (!receipt) return;
                  if (isConnected) {
                    try { await silentPrintOrder(receipt); showToast('Bill printed successfully', 'success'); return; } catch (err) {
                      console.error('[Printer] silentPrintOrder failed:', err);
                      showToast('Printer error — switching to browser print', 'warning');
                    }
                  } else {
                    showToast('Printer app not connected — printing via browser', 'info');
                  }
                  await new Promise((r) => setTimeout(r, 1500));
                  browserPrintOrder(receipt);
                }}
                sx={{
                  flex: 1,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  borderRadius: '8px',
                  bgcolor: '#4F46E5',
                  '&:hover': { bgcolor: '#4338CA' },
                }}
              >
                Print Invoice
              </Button>
            </Box>
          )}

          {/* Send / resend on WhatsApp — its own 3-send limit, separate from the billing screen's */}
          {order?.status === 'paid' && !isFetching && (
            <Box sx={{ mx: { xs: -1.5, sm: -2 }, mt: 1, mb: -1.25 }}>
              <WhatsAppSection key={order.id} order={order} receipt={receipt} source="order_history"
                customerWhatsappOptIn={customer?.whatsapp_opt_in ?? null} />
            </Box>
          )}
        </Box>

        {/* Body */}
        <Box sx={{
          flexGrow: 1, overflowY: 'auto', p: 2,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
        }}>
          {isFetching ? (
            <Stack spacing={1.5}>
              {[...Array(5)].map((_, i) => <Skeleton key={i} height={56} sx={{ borderRadius: '10px' }} variant="rectangular" />)}
            </Stack>
          ) : !order ? (
            <Box sx={{ textAlign: 'center', py: 6, color: '#94A3B8' }}>
              <Receipt sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
              <Typography fontSize="0.85rem" fontWeight={600}>Order not found</Typography>
            </Box>
          ) : (
            <Stack spacing={2}>

              {/* Status + Customer + Salesperson */}
              <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0', p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <StatusBadge status={order.status} />
                  {(order.customer_name || order.customer?.name) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: '#EEF2FF', color: '#4F46E5', fontSize: '0.72rem', fontWeight: 800 }}>
                        {(order.customer_name || order.customer?.name)?.[0]?.toUpperCase()}
                      </Avatar>
                      <Box sx={{ textAlign: 'right' }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end">
                          <Typography fontSize="0.8rem" fontWeight={700} color="#1E293B">
                            {order.customer_name || order.customer?.name}
                          </Typography>
                          {order.customer_active === false && (
                            <Chip size="small" label="Inactive" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#FEF2F2', color: '#DC2626' }} />
                          )}
                        </Stack>
                        {order.customer?.phone_number && (
                          <Typography fontSize="0.68rem" color="#94A3B8">{order.customer.phone_number}</Typography>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <Typography fontSize="0.75rem" color="#CBD5E1" fontStyle="italic">Walk-in customer</Typography>
                  )}
                </Box>
                {order.salesperson_name && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25, pt: 1.25, borderTop: '1px solid #F1F5F9' }}>
                    <Person sx={{ fontSize: 14, color: '#10B981' }} />
                    <Typography fontSize="0.75rem" color="#64748B">Salesperson</Typography>
                    <Typography fontSize="0.75rem" fontWeight={700} color="#10B981" sx={{ ml: 'auto' }}>
                      {order.salesperson_name}
                    </Typography>
                  </Box>
                )}
                {order.cashier_name && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25, pt: 1.25, borderTop: '1px solid #F1F5F9' }}>
                    <Person sx={{ fontSize: 14, color: '#94A3B8' }} />
                    <Typography fontSize="0.75rem" color="#64748B">Cashier</Typography>
                    <Typography fontSize="0.75rem" fontWeight={700} color="#1E293B" sx={{ ml: 'auto' }}>
                      {order.cashier_name}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Items */}
              <Box>
                <Typography fontSize="0.68rem" fontWeight={800} color="#94A3B8" letterSpacing="0.06rem" sx={{ mb: 1, px: 0.5 }}>
                  ORDER ITEMS
                </Typography>
                <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                  {(order.items || order.order_items || []).map((item, i) => (
                    <React.Fragment key={item.id || i}>
                      {i > 0 && <Divider />}
                      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box sx={{
                          width: 32, height: 32, borderRadius: '8px', bgcolor: '#F1F5F9',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          <ShoppingCart sx={{ fontSize: 14, color: '#94A3B8' }} />
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography fontSize="0.82rem" fontWeight={700} color="#1E293B" noWrap>
                              {item.product_name}
                            </Typography>
                            {item.product_active === false && (
                              <Chip size="small" label="Inactive" sx={{ height: 17, fontSize: '0.58rem', fontWeight: 700, bgcolor: '#FEF2F2', color: '#DC2626', flexShrink: 0 }} />
                            )}
                          </Stack>
                          <Typography fontSize="0.68rem" color="#94A3B8">
                            {item.product_sku} · ×{item.quantity} @ ₹{parseFloat(item.unit_price || 0).toFixed(2)}
                          </Typography>
                          {item.salesperson_name && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.4 }}>
                              <Person sx={{ fontSize: 11, color: '#10B981' }} />
                              <Typography fontSize="0.65rem" color="#10B981" fontWeight={600}>
                                {item.salesperson_name}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography fontSize="0.85rem" fontWeight={700} color="#1E293B">
                            ₹{parseFloat(item.line_total || 0).toFixed(2)}
                          </Typography>
                          {parseFloat(item.tax_amount || 0) > 0 && (
                            <Typography fontSize="0.65rem" color="#94A3B8">incl. ₹{parseFloat(item.tax_amount).toFixed(2)} tax</Typography>
                          )}
                          {parseFloat(item.discount_amount || 0) > 0 && (
                            <Typography fontSize="0.65rem" color="#16A34A">−₹{parseFloat(item.discount_amount).toFixed(2)}</Typography>
                          )}
                        </Box>
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              </Box>

              {/* Totals */}
              <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0', p: 2 }}>
                <Typography fontSize="0.68rem" fontWeight={800} color="#94A3B8" letterSpacing="0.06rem" sx={{ mb: 1.25 }}>
                  BILL SUMMARY
                </Typography>
                <Stack spacing={0.75}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography fontSize="0.8rem" color="#64748B">Subtotal</Typography>
                    <Typography fontSize="0.8rem" color="#1E293B" fontWeight={600}>₹{parseFloat(order.subtotal || 0).toFixed(2)}</Typography>
                  </Box>
                  {parseFloat(order.tax_total || 0) > 0 && (
                    <>
                      {order.is_intrastate ? (
                        <>
                          {parseFloat(order.cgst_total || 0) > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography fontSize="0.8rem" color="#64748B">CGST</Typography>
                              <Typography fontSize="0.8rem" color="#64748B">₹{parseFloat(order.cgst_total).toFixed(2)}</Typography>
                            </Box>
                          )}
                          {parseFloat(order.sgst_total || 0) > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography fontSize="0.8rem" color="#64748B">SGST</Typography>
                              <Typography fontSize="0.8rem" color="#64748B">₹{parseFloat(order.sgst_total).toFixed(2)}</Typography>
                            </Box>
                          )}
                        </>
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography fontSize="0.8rem" color="#64748B">IGST</Typography>
                          <Typography fontSize="0.8rem" color="#64748B">₹{parseFloat(order.igst_total).toFixed(2)}</Typography>
                        </Box>
                      )}
                    </>
                  )}
                  {parseFloat(order.discount_total || 0) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography fontSize="0.8rem" color="#64748B">Discount</Typography>
                      <Typography fontSize="0.8rem" color="#16A34A">−₹{parseFloat(order.discount_total).toFixed(2)}</Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography fontSize="0.88rem" fontWeight={800} color="#1E293B">Total</Typography>
                    <Typography fontSize="1rem" fontWeight={900} color="#4F46E5">₹{parseFloat(order.grand_total || 0).toFixed(2)}</Typography>
                  </Box>
                </Stack>
              </Box>

              {/* GST E-invoice — read-only, no action buttons. This app has
                  no route to trigger/retry generation; a "Failed" status is
                  a signal to flag a manager or admin, not to retry here. */}
              {order.customer_gstin && (
                <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0', p: 2 }}>
                  <Typography fontSize="0.68rem" fontWeight={800} color="#94A3B8" letterSpacing="0.06rem" sx={{ mb: 1.25 }}>
                    GST E-INVOICE
                  </Typography>
                  <Stack spacing={0.9}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography fontSize="0.8rem" color="#64748B">IRN Status</Typography>
                      <EinvoiceStatus status={order.e_invoice?.status} />
                    </Box>
                    {order.e_invoice?.irn && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography fontSize="0.8rem" color="#64748B">IRN</Typography>
                        <Typography fontSize="0.72rem" color="#1E293B" fontWeight={600} sx={{ fontFamily: 'monospace', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {order.e_invoice.irn}
                        </Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 0.25 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography fontSize="0.8rem" color="#64748B">E-Way Bill</Typography>
                      <EinvoiceStatus status={order.eway_bill?.status} />
                    </Box>
                    {order.eway_bill?.number && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography fontSize="0.8rem" color="#64748B">E-Way Bill No.</Typography>
                          <Typography fontSize="0.72rem" color="#1E293B" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                            {order.eway_bill.number}
                          </Typography>
                        </Box>
                        {order.eway_bill.vehicle_number && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography fontSize="0.8rem" color="#64748B">Vehicle</Typography>
                            <Typography fontSize="0.72rem" color="#1E293B" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                              {order.eway_bill.vehicle_number}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Stack>
                </Box>
              )}

              {/* Payments */}
              {order.status === 'paid' && (order.payments || []).length > 0 && (
                <Box>
                  <Typography fontSize="0.68rem" fontWeight={800} color="#94A3B8" letterSpacing="0.06rem" sx={{ mb: 1, px: 0.5 }}>
                    PAYMENT RECEIVED
                  </Typography>
                  <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                    {order.payments.map((pmt, i) => (
                      <React.Fragment key={pmt.id || i}>
                        {i > 0 && <Divider />}
                        <Box sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <CurrencyRupee sx={{ fontSize: 14, color: '#10B981' }} />
                            </Box>
                            <Typography fontSize="0.82rem" fontWeight={600} color="#374151">
                              {typeof pmt.payment_mode === 'string' ? pmt.payment_mode : (pmt.payment_mode?.name || 'Unknown')}
                            </Typography>
                          </Box>
                          <Typography fontSize="0.85rem" fontWeight={700} color="#059669">
                            ₹{parseFloat(pmt.amount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Loyalty */}
              {parseFloat(order.loyalty_points_earned || 0) > 0 && (
                <Box sx={{ bgcolor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '12px', px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography fontSize="0.8rem" fontWeight={600} color="#92400E">Loyalty Points Earned</Typography>
                  <Typography fontSize="0.88rem" fontWeight={800} color="#D97706">+{parseFloat(order.loyalty_points_earned).toFixed(0)} pts</Typography>
                </Box>
              )}

              {/* Returns + Exchanges history tabs */}
              {(isReturnsLoading || isExchangesLoading || orderReturns.length > 0 || orderExchanges.length > 0) && (
                <Box>
                  {/* Tab switcher */}
                  <Box sx={{ display: 'flex', gap: 0, mb: 1, border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden', width: 'fit-content' }}>
                    {[{ key: 'returns', label: 'Returns' }, { key: 'exchanges', label: 'Exchanges' }].map((tab) => (
                      <Box
                        key={tab.key}
                        onClick={() => setDetailTab(tab.key)}
                        sx={{
                          px: 1.5, py: 0.6, cursor: 'pointer',
                          bgcolor: detailTab === tab.key ? '#EDE9FE' : 'transparent',
                          '&:hover': { bgcolor: detailTab === tab.key ? '#EDE9FE' : '#F8FAFC' },
                        }}
                      >
                        <Typography
                          fontSize="0.72rem" fontWeight={700}
                          color={detailTab === tab.key ? '#7C3AED' : '#94A3B8'}
                        >
                          {tab.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {detailTab === 'returns' ? (
                    isReturnsLoading ? (
                      <Stack spacing={1}>
                        <Skeleton height={64} sx={{ borderRadius: '10px' }} variant="rectangular" />
                        <Skeleton height={64} sx={{ borderRadius: '10px' }} variant="rectangular" />
                      </Stack>
                    ) : orderReturns.length === 0 ? (
                      <Typography fontSize="0.78rem" color="#94A3B8" fontStyle="italic" sx={{ py: 1 }}>
                        No returns for this order.
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        {orderReturns.map((ret) => (
                          <ReturnHistoryRow
                            key={ret.id}
                            orderReturn={ret}
                            orderId={orderId}
                            onOpenDetail={onOpenReturn ? (r) => onOpenReturn(r, orderId) : undefined}
                          />
                        ))}
                      </Stack>
                    )
                  ) : (
                    isExchangesLoading ? (
                      <Stack spacing={1}>
                        <Skeleton height={64} sx={{ borderRadius: '10px' }} variant="rectangular" />
                        <Skeleton height={64} sx={{ borderRadius: '10px' }} variant="rectangular" />
                      </Stack>
                    ) : orderExchanges.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography fontSize="0.78rem" color="#94A3B8" fontStyle="italic">
                          No exchanges for this order.
                        </Typography>
                        {order?.status === 'paid' && (
                          <Button
                            size="small" sx={{ mt: 1, textTransform: 'none', color: '#7C3AED', fontWeight: 700, fontSize: '0.75rem' }}
                            startIcon={<SwapHoriz sx={{ fontSize: 14 }} />}
                            onClick={() => onOpenExchangeCreate && onOpenExchangeCreate(order)}
                          >
                            Exchange Items
                          </Button>
                        )}
                      </Box>
                    ) : (
                      <Stack spacing={1}>
                        {orderExchanges.map((exc) => (
                          <ExchangeHistoryRow
                            key={exc.id}
                            exchange={exc}
                            onClick={() => { setSelectedExchangeId(exc.id); setExchangeDetailOpen(true); }}
                          />
                        ))}
                      </Stack>
                    )
                  )}
                </Box>
              )}

              {/* Order Notes */}
              {order.notes && (
                <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', px: 2, py: 1.25 }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <NoteAlt sx={{ fontSize: 16, color: '#64748B', mt: 0.15, flexShrink: 0 }} />
                    <Box>
                      <Typography fontSize="0.68rem" fontWeight={800} color="#94A3B8" letterSpacing="0.06rem" sx={{ mb: 0.25 }}>ORDER NOTE</Typography>
                      <Typography fontSize="0.82rem" color="#374151" sx={{ whiteSpace: 'pre-wrap' }}>{order.notes}</Typography>
                    </Box>
                  </Stack>
                </Box>
              )}

            </Stack>
          )}
        </Box>
      </Drawer>

      <ReturnItemsDrawer
        open={returnDrawerOpen}
        onClose={() => setReturnDrawerOpen(false)}
        order={order}
      />

      <ExchangeDetailDrawer
        open={exchangeDetailOpen}
        onClose={() => { setExchangeDetailOpen(false); setSelectedExchangeId(null); }}
        orderId={orderId}
        exchangeId={selectedExchangeId}
      />
    </>
  );
}

// ─── Status filter tabs ───────────────────────────────────────────────────────

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'paid', label: 'Paid' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'draft', label: 'Draft' },
  { value: 'cancelled', label: 'Cancelled' },
];

// ─── Skeleton rows ────────────────────────────────────────────────────────────

function RowSkeleton() {
  return (
    <TableRow>
      {[120, 130, 120, 60, 90, 70].map((w, i) => (
        <TableCell key={i} sx={{ py: 1.5, ...(i === 0 && { pl: 3 }) }}>
          <Skeleton width={w} height={18} />
        </TableCell>
      ))}
    </TableRow>
  );
}

// ─── Mobile card ─────────────────────────────────────────────────────────────

function OrderCard({ order, onClick }) {
  return (
    <Box
      onClick={() => onClick(order.id)}
      sx={{
        p: 2, borderRadius: '12px', bgcolor: '#fff', border: '1px solid #E2E8F0',
        cursor: 'pointer',
        '&:active': { bgcolor: '#F8FAFC' },
        '&:hover': { borderColor: '#C7D2FE', boxShadow: '0 2px 10px rgba(79,70,229,0.07)' },
        transition: 'all 0.15s',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.25 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography fontWeight={800} fontSize="0.9rem" color="#1E293B">{order.order_number || order.reference_number}</Typography>
            {order.is_exchange && (
              <Chip label="Exchange" size="small"
                sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#dbeafe', color: '#1d4ed8', px: 0.25 }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            <AccessTime sx={{ fontSize: 11, color: '#CBD5E1' }} />
            <Typography fontSize="0.68rem" color="#94A3B8">
              {new Date(order.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
            </Typography>
          </Box>
        </Box>
        <StatusBadge status={order.status} />
      </Box>
      <Divider sx={{ mb: 1.25 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        {/* Wraps onto a second line on narrow phones so the amount never gets pushed off. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, rowGap: 0.5, flexWrap: 'wrap', minWidth: 0 }}>
          {(order.customer_name || order.customer?.name) ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Person sx={{ fontSize: 13, color: '#CBD5E1' }} />
              <Typography fontSize="0.75rem" color="#64748B" fontWeight={600} noWrap sx={{ maxWidth: 120 }}>
                {order.customer_name || order.customer?.name}
              </Typography>
            </Box>
          ) : (
            <Typography fontSize="0.72rem" color="#CBD5E1" fontStyle="italic">Walk-in</Typography>
          )}
          {order.salesperson_name && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Person sx={{ fontSize: 11, color: '#10B981' }} />
              <Typography fontSize="0.68rem" color="#10B981" fontWeight={600} noWrap sx={{ maxWidth: 80 }}>
                {order.salesperson_name}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <ShoppingCart sx={{ fontSize: 12, color: '#CBD5E1' }} />
            <Typography fontSize="0.7rem" color="#94A3B8" sx={{ whiteSpace: 'nowrap' }}>
              {(() => { const n = order.items_count ?? order.order_items_count ?? 0; return `${n} item${n === 1 ? '' : 's'}`; })()} · Qty {fmtQty(order.total_quantity)}
            </Typography>
          </Box>
        </Box>
        <Typography fontWeight={800} fontSize="0.95rem" color="#4F46E5" sx={{ flexShrink: 0 }}>
          ₹{parseFloat(order.grand_total || 0).toFixed(2)}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function OrderHistory() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { openMobileSidebar } = useContext(SidebarContext);
  const navigate = useNavigate();

  const [mode, setMode] = useState('orders'); // 'orders' | 'returns'
  const [activeStatus, setActiveStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [returnStatus, setReturnStatus] = useState('');
  const [selectedReturnId, setSelectedReturnId] = useState(null);
  const [selectedReturnOrderId, setSelectedReturnOrderId] = useState(null);
  const [selectedReturnSummary, setSelectedReturnSummary] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [appliedDateFrom, setAppliedDateFrom] = useState('');
  const [appliedDateTo, setAppliedDateTo] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const { data, isFetching } = useListOrdersQuery({
    status: activeStatus || undefined,
    date_from: appliedDateFrom || undefined,
    date_to: appliedDateTo || undefined,
    page: page + 1,
    per_page: rowsPerPage,
  }, { skip: mode !== 'orders' });

  const orders = data?.data?.orders || (Array.isArray(data?.data) ? data.data : []);
  const totalCount = data?.data?.pagination?.total_count || data?.meta?.total_count || orders.length;

  const { data: returnsData, isFetching: isReturnsFetching } = useListStoreReturnsQuery({
    status: returnStatus || undefined,
    date_from: appliedDateFrom || undefined,
    date_to: appliedDateTo || undefined,
    page: page + 1,
    per_page: rowsPerPage,
  }, { skip: mode !== 'returns' });

  const storeReturns = returnsData?.data?.order_returns || [];
  const returnsTotalCount = returnsData?.data?.meta?.total_count || storeReturns.length;

  const handleApplyDates = () => {
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setPage(0);
  };

  const handleClearDates = () => {
    setDateFrom('');
    setDateTo('');
    setAppliedDateFrom('');
    setAppliedDateTo('');
    setPage(0);
  };

  const hasDateFilter = appliedDateFrom || appliedDateTo;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#F1F5F9', overflow: 'hidden' }}>

      {/* ── Page header ── */}
      <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.5, pb: 0, bgcolor: '#fff', borderBottom: '1px solid #E2E8F0', flexShrink: 0 }}>

        {/* Title row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Hamburger — mobile only */}
            <IconButton
              size="small"
              onClick={openMobileSidebar}
              sx={{
                display: { xs: 'flex', md: 'none' },
                bgcolor: '#F8FAFC', border: '1px solid #E2E8F0',
                borderRadius: '8px', width: 36, height: 36, color: '#1E293B',
                flexShrink: 0,
              }}
            >
              <MenuIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Box>
              <Typography fontWeight={900} fontSize={{ xs: '1rem', md: '1.15rem' }} color="#1E293B">
                Order History
              </Typography>
              <Typography fontSize="0.72rem" color="#94A3B8" sx={{ mt: 0.25 }}>
                {isFetching || isReturnsFetching
                  ? 'Loading…'
                  : mode === 'returns'
                    ? `${returnsTotalCount} return${returnsTotalCount !== 1 ? 's' : ''}`
                    : `${totalCount} record${totalCount !== 1 ? 's' : ''}`}
                {hasDateFilter && ` · filtered`}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={() => setShowDateFilter(f => !f)}
            sx={{
              border: '1px solid',
              borderColor: showDateFilter || hasDateFilter ? '#4F46E5' : '#E2E8F0',
              borderRadius: '8px', p: 0.75,
              color: showDateFilter || hasDateFilter ? '#4F46E5' : '#64748B',
              bgcolor: showDateFilter || hasDateFilter ? '#EEF2FF' : 'transparent',
            }}
          >
            <Tune sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Search + mode selector */}
        <Box sx={{ pb: 1.5, display: 'flex', gap: 0, alignItems: 'center' }}>
          <Box sx={{
            display: 'flex', alignItems: 'center',
            border: '1px solid #E2E8F0', borderRadius: '10px',
            overflow: 'hidden', bgcolor: '#fff', width: '100%',
            '&:focus-within': { borderColor: '#4F46E5', boxShadow: '0 0 0 2px #EEF2FF' },
            transition: 'all 0.15s',
          }}>
            {/* Type selector */}
            <Select
              value={mode}
              onChange={(e) => {
                setMode(e.target.value);
                setSearchQuery('');
                setReturnStatus('');
                setActiveStatus('');
                setPage(0);
              }}
              variant="standard"
              disableUnderline
              sx={{
                minWidth: { xs: 110, sm: 130 },
                flexShrink: 0,
                px: 1.5,
                py: 0.5,
                fontSize: '0.82rem',
                fontWeight: 700,
                color: mode === 'returns' ? '#C2410C' : '#4F46E5',
                borderRight: '1px solid #E2E8F0',
                '& .MuiSelect-select': { py: '6px', pr: '24px !important', display: 'flex', alignItems: 'center', gap: 0.5 },
              }}
              renderValue={(val) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {val === 'returns'
                    ? <AssignmentReturn sx={{ fontSize: 14, color: '#C2410C' }} />
                    : <Receipt sx={{ fontSize: 14, color: '#4F46E5' }} />}
                  <Typography fontSize="0.82rem" fontWeight={700} color={val === 'returns' ? '#C2410C' : '#4F46E5'}>
                    {val === 'returns' ? 'Returns' : 'Orders'}
                  </Typography>
                </Box>
              )}
            >
              <MenuItem value="orders">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Receipt sx={{ fontSize: 15, color: '#4F46E5' }} />
                  <Typography fontSize="0.85rem" fontWeight={600}>Orders</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="returns">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentReturn sx={{ fontSize: 15, color: '#C2410C' }} />
                  <Typography fontSize="0.85rem" fontWeight={600}>Returns</Typography>
                </Box>
              </MenuItem>
            </Select>

            {/* Search input */}
            <Box sx={{ flexGrow: 1, px: 1.5, display: 'flex', alignItems: 'center' }}>
              <input
                style={{
                  border: 'none', outline: 'none', background: 'transparent',
                  width: '100%', fontSize: '0.82rem', color: '#1E293B',
                  fontFamily: 'inherit',
                }}
                placeholder={mode === 'returns' ? 'Search return # or order #…' : 'Search order # or customer…'}
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              />
            </Box>
            {searchQuery ? (
              <IconButton size="small" onClick={() => { setSearchQuery(''); setPage(0); }} sx={{ mr: 0.5, color: '#94A3B8' }}>
                <Close sx={{ fontSize: 14 }} />
              </IconButton>
            ) : (
              <Box sx={{ pr: 1.5, display: 'flex', alignItems: 'center', color: '#CBD5E1' }}>
                <Search sx={{ fontSize: 16 }} />
              </Box>
            )}
          </Box>
        </Box>

        {/* Date filter row (collapsible) */}
        {showDateFilter && (
          <Box sx={{ pb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <TextField
              size="small" label="From" type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.82rem' } }}
            />
            <TextField
              size="small" label="To" type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.82rem' } }}
            />
            <Button
              size="small" variant="contained" disableElevation onClick={handleApplyDates}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' }, fontSize: '0.8rem' }}
            >
              Apply
            </Button>
            {hasDateFilter && (
              <Button
                size="small" onClick={handleClearDates}
                sx={{ textTransform: 'none', fontWeight: 700, color: '#94A3B8', fontSize: '0.8rem' }}
              >
                Clear
              </Button>
            )}
          </Box>
        )}

        {/* Status tabs */}
        <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', pb: 0, '&::-webkit-scrollbar': { display: 'none' } }}>
          {mode === 'returns' ? (
            [{ value: '', label: 'All' }, { value: 'completed', label: 'Completed' }, { value: 'draft', label: 'Draft' }].map((tab) => {
              const active = returnStatus === tab.value;
              return (
                <Box
                  key={tab.value}
                  onClick={() => { setReturnStatus(tab.value); setPage(0); }}
                  sx={{
                    px: 1.5, py: 1, cursor: 'pointer', flexShrink: 0,
                    borderBottom: active ? '2px solid #C2410C' : '2px solid transparent',
                    mb: '-1px',
                  }}
                >
                  <Typography
                    fontSize="0.8rem" fontWeight={active ? 800 : 600}
                    color={active ? '#C2410C' : '#94A3B8'}
                    sx={{ transition: 'color 0.15s' }}
                  >
                    {tab.label}
                  </Typography>
                </Box>
              );
            })
          ) : (
            STATUS_TABS.map((tab) => {
              const active = activeStatus === tab.value;
              return (
                <Box
                  key={tab.value}
                  onClick={() => { setActiveStatus(tab.value); setPage(0); }}
                  sx={{
                    px: 1.5, py: 1, cursor: 'pointer', flexShrink: 0,
                    borderBottom: active ? '2px solid #4F46E5' : '2px solid transparent',
                    mb: '-1px',
                  }}
                >
                  <Typography
                    fontSize="0.8rem" fontWeight={active ? 800 : 600}
                    color={active ? '#4F46E5' : '#94A3B8'}
                    sx={{ transition: 'color 0.15s' }}
                  >
                    {tab.label}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {isMobile ? (
          /* Mobile card list */
          <Box sx={{
            flexGrow: 1, overflowY: 'auto', px: 2, py: 2,
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
          }}>
            {mode === 'returns' ? (
              /* Returns mobile cards */
              isReturnsFetching ? (
                <Stack spacing={1.25}>
                  {[...Array(6)].map((_, i) => <Skeleton key={i} height={104} sx={{ borderRadius: '12px' }} variant="rectangular" />)}
                </Stack>
              ) : storeReturns.length === 0 ? (
                <ReturnsEmptyState />
              ) : (
                <Stack spacing={1.25}>
                  {storeReturns.map((ret) => (
                    <ReturnCard
                      key={ret.id}
                      orderReturn={ret}
                      onClick={() => { setSelectedReturnId(ret.id); setSelectedReturnOrderId(ret.order_id); setSelectedReturnSummary(ret); }}
                    />
                  ))}
                </Stack>
              )
            ) : (
              /* Orders mobile cards */
              isFetching ? (
                <Stack spacing={1.25}>
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} height={104} sx={{ borderRadius: '12px' }} variant="rectangular" />
                  ))}
                </Stack>
              ) : orders.length === 0 ? (
                <EmptyState hasFilter={!!activeStatus || hasDateFilter} onClear={() => { setActiveStatus(''); handleClearDates(); }} />
              ) : (
                <Stack spacing={1.25}>
                  {orders.map(order => (
                    <OrderCard key={order.id} order={order} onClick={setSelectedOrderId} />
                  ))}
                </Stack>
              )
            )}
          </Box>
        ) : (
          /* Desktop table */
          mode === 'returns' ? (
            <Box sx={{ flexGrow: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: '6px', height: '6px' }, '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' } }}>
              <Table stickyHeader size="small" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                <TableHead>
                  <TableRow>
                    {['Return No', 'Order No', 'Refund Method', 'Amount', 'Status', 'Date'].map((h, i) => (
                      <TableCell key={i} sx={{
                        bgcolor: '#fff', fontWeight: 800, fontSize: '0.68rem', color: '#94A3B8',
                        letterSpacing: '0.06rem', textTransform: 'uppercase',
                        borderBottom: '1px solid #E2E8F0', py: 1.5,
                        pl: i === 0 ? 3 : 2, pr: 2, whiteSpace: 'nowrap',
                      }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isReturnsFetching ? (
                    [...Array(8)].map((_, i) => (
                      <TableRow key={i}>
                        {[160, 140, 130, 90, 90, 80].map((w, j) => (
                          <TableCell key={j} sx={{ py: 1.5, ...(j === 0 && { pl: 3 }) }}>
                            <Skeleton width={w} height={18} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : storeReturns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ border: 'none', py: 0 }}>
                        <ReturnsEmptyState />
                      </TableCell>
                    </TableRow>
                  ) : (
                    storeReturns.map((ret, idx) => {
                      const isCompleted = ret.status === 'completed';
                      const methodColors = {
                        cash: { bgcolor: '#EFF6FF', color: '#2563EB' },
                        store_credit: { bgcolor: '#F5F3FF', color: '#7C3AED' },
                        same_payment_mode: { bgcolor: '#EEF2FF', color: '#4F46E5' },
                      };
                      const methodLabels = { cash: 'Cash', store_credit: 'Store Credit', same_payment_mode: 'Same Payment Mode' };
                      const methodCfg = methodColors[ret.refund_method] || { bgcolor: '#F1F5F9', color: '#64748B' };
                      return (
                        <TableRow
                          key={ret.id}
                          onClick={() => { setSelectedReturnId(ret.id); setSelectedReturnOrderId(ret.order_id); setSelectedReturnSummary(ret); }}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: idx % 2 === 0 ? '#fff' : '#FAFAFA',
                            '&:hover': { bgcolor: '#FFF7ED' },
                            '& td': { borderBottom: '1px solid #F1F5F9' },
                            transition: 'background 0.1s',
                          }}
                        >
                          <TableCell sx={{ pl: 3, pr: 2, py: 1.5 }}>
                            <Typography fontWeight={800} fontSize="0.83rem" color="#C2410C">
                              {ret.return_number}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            <Typography fontSize="0.8rem" color="#4F46E5" fontWeight={600}>
                              {ret.order_number}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            <Chip label={methodLabels[ret.refund_method] || ret.refund_method} size="small"
                              sx={{ fontWeight: 700, fontSize: '0.68rem', height: 20, ...methodCfg }} />
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            {isCompleted && ret.refund_amount ? (
                              <Typography fontSize="0.85rem" fontWeight={800} color="#15803D" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                ₹{parseFloat(ret.monetary_refund_amount ?? ret.refund_amount).toFixed(2)}
                              </Typography>
                            ) : (
                              <Typography fontSize="0.82rem" color="#94A3B8" fontStyle="italic">—</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            <Box sx={{
                              display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.35,
                              borderRadius: '6px', bgcolor: isCompleted ? '#ECFDF5' : '#FFFBEB',
                            }}>
                              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: isCompleted ? '#10B981' : '#F59E0B' }} />
                              <Typography fontSize="0.7rem" fontWeight={700} color={isCompleted ? '#059669' : '#B45309'} lineHeight={1}>
                                {isCompleted ? 'Completed' : 'Draft'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <AccessTime sx={{ fontSize: 13, color: '#CBD5E1' }} />
                              <Typography fontSize="0.78rem" color="#64748B">
                                {new Date(ret.completed_at || ret.created_at).toLocaleDateString('en-IN', { dateStyle: 'short' })}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Box sx={{ flexGrow: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: '6px', height: '6px' }, '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' } }}>
              <Table stickyHeader size="small" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                <TableHead>
                  <TableRow>
                    {['Order No', 'Date & Time', 'Customer', 'Items', 'Qty', 'Total', 'Status'].map((h, i) => (
                      <TableCell
                        key={i}
                        sx={{
                          bgcolor: '#fff',
                          fontWeight: 800, fontSize: '0.68rem', color: '#94A3B8',
                          letterSpacing: '0.06rem', textTransform: 'uppercase',
                          borderBottom: '1px solid #E2E8F0', py: 1.5,
                          pl: i === 0 ? 3 : 2,
                          pr: 2,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isFetching ? (
                    [...Array(10)].map((_, i) => <RowSkeleton key={i} />)
                  ) : orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ border: 'none', py: 0 }}>
                        <EmptyState hasFilter={!!activeStatus || hasDateFilter} onClear={() => { setActiveStatus(''); handleClearDates(); }} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((order, idx) => (
                      <TableRow
                        key={order.id}
                        onClick={() => setSelectedOrderId(order.id)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: idx % 2 === 0 ? '#fff' : '#FAFAFA',
                          '&:hover': { bgcolor: '#F0F4FF' },
                          '& td': { borderBottom: '1px solid #F1F5F9' },
                          transition: 'background 0.1s',
                        }}
                      >
                        <TableCell sx={{ pl: 3, pr: 2, py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography fontWeight={800} fontSize="0.83rem" color="#4F46E5">
                              {order.order_number || order.reference_number}
                            </Typography>
                            {order.is_exchange && (
                              <Chip label="Exchange" size="small"
                                sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#dbeafe', color: '#1d4ed8', px: 0.25 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ px: 2, py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <AccessTime sx={{ fontSize: 13, color: '#CBD5E1' }} />
                            <Typography fontSize="0.78rem" color="#64748B">
                              {new Date(order.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ px: 2, py: 1.5 }}>
                          {(order.customer_name || order.customer?.name) ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Avatar sx={{ width: 22, height: 22, bgcolor: '#EEF2FF', color: '#4F46E5', fontSize: '0.6rem', fontWeight: 800 }}>
                                {(order.customer_name || order.customer?.name)?.[0]?.toUpperCase()}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography fontSize="0.8rem" color="#374151" fontWeight={600} noWrap sx={{ maxWidth: 140 }}>
                                  {order.customer_name || order.customer?.name}
                                </Typography>
                                {order.salesperson_name && (
                                  <Typography fontSize="0.65rem" color="#10B981" fontWeight={600} noWrap sx={{ maxWidth: 140 }}>
                                    SP: {order.salesperson_name}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ) : (
                            <Box>
                              <Typography fontSize="0.78rem" color="#CBD5E1" fontStyle="italic">Walk-in</Typography>
                              {order.salesperson_name && (
                                <Typography fontSize="0.65rem" color="#10B981" fontWeight={600}>
                                  SP: {order.salesperson_name}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ px: 2, py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ShoppingCart sx={{ fontSize: 13, color: '#CBD5E1' }} />
                            <Typography fontSize="0.8rem" color="#64748B" fontWeight={600}>
                              {order.items_count ?? order.order_items_count ?? 0}
                            </Typography>
                          </Box>
                        </TableCell>
                        {/* Units sold — Items counts distinct products only */}
                        <TableCell sx={{ px: 2, py: 1.5 }}>
                          <Typography fontSize="0.8rem" color="#1E293B" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {fmtQty(order.total_quantity)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ px: 2, py: 1.5 }}>
                          <Typography fontSize="0.85rem" fontWeight={800} color="#1E293B" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            ₹{parseFloat(order.grand_total || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ px: 2, py: 1.5 }}>
                          <StatusBadge status={order.status} />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          )
        )}

        {/* Pagination */}
        {mode === 'returns' ? (
          !isReturnsFetching && storeReturns.length > 0 && (
            <Box sx={{ borderTop: '1px solid #E2E8F0', bgcolor: '#fff', flexShrink: 0 }}>
              <TablePagination
                component="div"
                count={returnsTotalCount}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{
                  '.MuiTablePagination-toolbar': { minHeight: 44 },
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.78rem', color: '#64748B' },
                }}
              />
            </Box>
          )
        ) : (
          !isFetching && orders.length > 0 && (
            <Box sx={{ borderTop: '1px solid #E2E8F0', bgcolor: '#fff', flexShrink: 0 }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{
                  '.MuiTablePagination-toolbar': { minHeight: 44 },
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.78rem', color: '#64748B' },
                }}
              />
            </Box>
          )
        )}
      </Box>

      {/* Detail drawer */}
      <OrderDetailDrawer
        orderId={selectedOrderId}
        open={Boolean(selectedOrderId)}
        onClose={() => setSelectedOrderId(null)}
        onOpenReturn={(ret, fromOrderId) => {
          setSelectedReturnId(ret.id);
          setSelectedReturnOrderId(fromOrderId || ret.order_id);
          setSelectedReturnSummary(ret);
        }}
        onOpenExchangeCreate={(order) => {
          setSelectedOrderId(null);
          navigate('/dashboard', { state: { exchangeOrderId: order.id } });
        }}
      />

      {/* Return detail drawer */}
      <ReturnDetailDrawer
        open={Boolean(selectedReturnId)}
        onClose={() => { setSelectedReturnId(null); setSelectedReturnOrderId(null); setSelectedReturnSummary(null); }}
        orderReturn={selectedReturnSummary || storeReturns.find((r) => r.id === selectedReturnId) || null}
        orderId={selectedReturnOrderId}
        isLoading={false}
      />

    </Box>
  );
}

function EmptyState({ hasFilter, onClear }) {
  return (
    <Box sx={{ textAlign: 'center', py: 10 }}>
      <Box sx={{ width: 64, height: 64, borderRadius: '16px', bgcolor: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
        <Receipt sx={{ fontSize: 28, color: '#CBD5E1' }} />
      </Box>
      <Typography fontWeight={700} color="#64748B" fontSize="0.9rem" sx={{ mb: 0.5 }}>
        No orders found
      </Typography>
      <Typography fontSize="0.78rem" color="#94A3B8">
        {hasFilter ? 'Try changing or clearing your filters' : 'Orders will appear here once created'}
      </Typography>
      {hasFilter && (
        <Button size="small" onClick={onClear}
          sx={{ mt: 2, textTransform: 'none', fontWeight: 700, color: '#4F46E5', fontSize: '0.8rem', borderRadius: '8px' }}>
          Clear filters
        </Button>
      )}
    </Box>
  );
}
